import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WebSocketProvider, useWebSocket } from './context/WebSocketContext';
import { MainLayout } from './components/layout/MainLayout';
import { Toaster } from './components/ui/sonner';
import IncomingCallNotification from './components/IncomingCallNotification';
import VoiceClient from './components/VoiceClient';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import ClientForm from './pages/ClientForm';
import Vendors from './pages/Vendors';
import VendorForm from './pages/VendorForm';
import WorkOrders from './pages/WorkOrders';
import WorkOrderForm from './pages/WorkOrderForm';
import CalendarView from './pages/CalendarView';
import Tasks from './pages/Tasks';
import Files from './pages/Files';
import Invoices from './pages/Invoices';
import Payables from './pages/Payables';
import VendorInvoices from './pages/VendorInvoices';
import ConsolidatedInvoicing from './pages/ConsolidatedInvoicing';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import VendorPortal from './pages/VendorPortal';
import ClientPortal from './pages/ClientPortal';
import RequestService from './pages/RequestService';
import PaymentSuccess from './pages/PaymentSuccess';
import WisconsinRequest from './pages/WisconsinRequest';
import VendorPublicProfile from './pages/VendorPublicProfile';
import KnowledgeBase from './pages/KnowledgeBase';
import VendorPayroll from './pages/VendorPayroll';
import Payments from './pages/Payments';
import EncounterForm from './pages/EncounterForm';
import EncounterDashboard from './pages/EncounterDashboard';
import ReportsDashboard from './pages/ReportsDashboard';
import CommunicationsDashboard from './pages/CommunicationsDashboard';
import Profile from './pages/Profile';
import BlastCommunications from './pages/BlastCommunications';

// Global Notification Component
const GlobalNotifications = () => {
  const { incomingCall, dismissIncomingCall } = useWebSocket();
  
  return (
    <>
      {incomingCall && (
        <IncomingCallNotification 
          notification={incomingCall} 
          onDismiss={dismissIncomingCall} 
        />
      )}
      <VoiceClient />
    </>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center mx-auto mb-4 animate-pulse">
            <span className="text-white font-bold text-2xl">B</span>
          </div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Public Route Component (redirects to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();
  
  // Check for session_id in URL hash - must be synchronous, not in useEffect
  // This handles the OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      
      {/* Public Portal Routes (separate from main app) */}
      <Route path="/vendor-portal" element={<VendorPortal />} />
      <Route path="/client-portal" element={<ClientPortal />} />
      <Route path="/encounter-form/:assignmentId" element={<EncounterForm />} />
      <Route path="/request-service" element={<RequestService />} />
      <Route path="/get-started" element={<RequestService />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/request/wisconsin" element={<WisconsinRequest />} />
      <Route path="/vendor-profile/:slug" element={<VendorPublicProfile />} />
      
      {/* Protected Routes */}
      <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        
        {/* Clients */}
        <Route path="clients" element={<Clients />} />
        <Route path="clients/new" element={<ClientForm />} />
        <Route path="clients/:clientId" element={<ClientForm />} />
        <Route path="clients/:clientId/edit" element={<ClientForm />} />
        
        {/* Vendors */}
        <Route path="vendors" element={<Vendors />} />
        <Route path="vendors/new" element={<VendorForm />} />
        <Route path="vendors/:vendorId" element={<VendorForm />} />
        <Route path="vendors/:vendorId/edit" element={<VendorForm />} />
        
        {/* Work Orders */}
        <Route path="work-orders" element={<WorkOrders />} />
        <Route path="work-orders/new" element={<WorkOrderForm />} />
        <Route path="work-orders/:workOrderId" element={<WorkOrderForm />} />
        <Route path="work-orders/:workOrderId/edit" element={<WorkOrderForm />} />
        
        {/* Calendar */}
        <Route path="calendar" element={<CalendarView />} />
        
        {/* Tasks */}
        <Route path="tasks" element={<Tasks />} />
        
        {/* Files */}
        <Route path="files" element={<Files />} />
        
        {/* Encounter Forms */}
        <Route path="encounters" element={<EncounterDashboard />} />
        
        {/* Finance */}
        <Route path="invoices" element={<Invoices />} />
        <Route path="consolidated-invoicing" element={<ConsolidatedInvoicing />} />
        <Route path="payables" element={<Payables />} />
        <Route path="vendor-invoices" element={<VendorInvoices />} />
        <Route path="vendor-payroll" element={<VendorPayroll />} />
        <Route path="payments" element={<Payments />} />
        
        {/* Reports */}
        <Route path="reports" element={<Reports />} />
        <Route path="reports-dashboard" element={<ReportsDashboard />} />
        
        {/* Communications */}
        <Route path="communications" element={<CommunicationsDashboard />} />
        <Route path="blasts" element={<BlastCommunications />} />
        
        {/* Knowledge Base */}
        <Route path="knowledge-base" element={<KnowledgeBase />} />
        <Route path="knowledge-base/:slug" element={<KnowledgeBase />} />
        
        {/* Settings & Profile */}
        <Route path="settings" element={<Settings />} />
        <Route path="profile" element={<Profile />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WebSocketProvider>
          <AppRouter />
          <GlobalNotifications />
          <Toaster position="top-right" richColors />
        </WebSocketProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
